import * as React from "react";
import { observer } from "mobx-react-lite";
import { Button, Dropdown, Layout, Popconfirm, Tabs } from "antd";
import ActivitiesCard from "../components/ActivitiesCard";
import { useDashboardStore } from "../stores/context";
import { useTasksStore } from "../../tasks/stores/context";
import { useStore } from "../../common/stores";
import AddWidgetDialog from "../components/AddWidgetDialog";
import { DashboardWidgetType } from "src/modules/administration/types/DashboardWidget";
import ListWidgetCard from "../components/ListWidgetCard";
import ListWidget from "../models/ListWidget";
import PieWidgetCard from "../components/PieWidgetCard";
import PieChartWidget from "../models/PieChartWidget";
import GroupedWidget from "../models/GroupedWidget";
import GroupedWidgetsCard from "../components/GroupedWidgetsCard";
import { LoadingIndicator } from "../../common/components/LoadingIndicator";
import CreateWidgetDialog from "../../administration/components/CreateWidgetDialog";
import { useAdminStore } from "../../administration/stores/context";
import CreateGroupedWidgetDialog from "src/modules/administration/components/CreateGroupedWidgetDialog";
import NumericWidgetCard from "../components/NumericWidgetCard";
import NumericWidget from "../models/NumericWidget";
import UpdateViewDialog from "../components/UpdateViewDialog";
import { DEFAULT_DASHBOARD_VIEW_KEY } from "../models/WidgetTypes";
import {
    TileLayout,
    TileLayoutRepositionEvent,
    TilePosition,
} from "@progress/kendo-react-layout";

const DashboardPage: React.FC = () => {
    const context = useDashboardStore();
    const tasksContext = useTasksStore();
    const userProfile = useStore().userProfileStore.userProfile;
    const { dashboardStore } = context;
    const { taskPreviewVisualStore } = tasksContext;

    const [isAddWidgetDialogVisible, setIsAddWidgetDialogVisible] =
        React.useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = React.useState(false);
    const [isUpdateViewDialogVisible, setIsUpdateViewDialogVisible] =
        React.useState(false);
    const [editableView, setEditableView] = React.useState("");

    const [
        isEditGroupedWidgetModalVisible,
        setIsEditGroupedWidgetModalVisible,
    ] = React.useState(false);

    const getColumns = () => {
        if (window.innerWidth < 600) return 1;
        if (window.innerWidth < 1280) return 2;
        return 3;
    };
    const [columns, setColumns] = React.useState(getColumns());

    const [tilePositions, setTilePositions] = React.useState(
        [] as TilePosition[],
    );

    React.useEffect(() => {
        if (dashboardStore.widgets.length && dashboardStore.views.length) {
            setPositions();
        }
    }, [columns, dashboardStore.widgets.length]);

    const setPositions = () => {
        const items = localStorage.getItem(dashboardStore.currentView);
        if (items) {
            const positions = JSON.parse(items);
            if (dashboardStore.getWidgetsCountInView() !== positions.length) {
                // Looks like it is another logged in user, so reset positions in storage as they are unsynced
                localStorage.removeItem(dashboardStore.currentView);
            } else {
                setTilePositions(positions);
                return;
            }
        }
        if (dashboardStore.currentView === DEFAULT_DASHBOARD_VIEW_KEY) {
            setTilePositions([
                { col: 1, colSpan: 1, rowSpan: 1 },
                ...dashboardStore.widgets.map((w, index) => ({
                    id: w.id,
                    col: ((index + 1) % columns) + 1,
                    colSpan: 1,
                    rowSpan: 1,
                })),
            ]);
        } else {
            setTilePositions(
                dashboardStore.widgets.map((w, index) => ({
                    col: (index % columns) + 1,
                    id: w.id,
                    colSpan: 1,
                    rowSpan: 1,
                })),
            );
        }
    };

    const adminContext = useAdminStore();
    const widgetConstructorStore = adminContext.widgetConstructorStore;

    React.useEffect(() => {
        widgetConstructorStore.init();
        dashboardStore.loadInitialData();
        const onResize = () => setColumns(getColumns());
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    React.useEffect(() => {
        if (dashboardStore.projects.length) {
            dashboardStore.setSelectedActivitiesProjectId(
                dashboardStore.projects[0].id,
            );
        }
    }, [dashboardStore.projects]);

    if (!userProfile) {
        return null;
    }

    const openEditWidgetModal = (widgetId: string) => {
        widgetConstructorStore.setEditableWidget(widgetId);
        setIsEditModalVisible(true);
    };

    const openEditGroupedWidgetModal = (widgetId: string) => {
        widgetConstructorStore.setEditableWidget(widgetId);
        setIsEditGroupedWidgetModalVisible(true);
    };

    const getWidgetsContainer = () => {
        if (dashboardStore.isLoading || widgetConstructorStore.isLoading) {
            return <LoadingIndicator />;
        }

        if (dashboardStore.getWidgetsCountInView() !== tilePositions.length) {
            setPositions();
        }

        // Create a map of widget ID to position index for sorting
        const positionIndexMap = new Map(
            tilePositions.map((pos, index) => [
                (pos as TilePosition & { id?: string }).id,
                index,
            ]),
        );

        const widgets = dashboardStore.widgets
            .slice()
            .sort((a, b) => {
                // First sort by default widget status
                const defaultDiff =
                    Number(b.isDefaultWidget) - Number(a.isDefaultWidget);
                if (defaultDiff !== 0) return defaultDiff;

                // Then sort by position in tilePositions array
                const indexA = positionIndexMap.get(a.id) ?? Number.MAX_VALUE;
                const indexB = positionIndexMap.get(b.id) ?? Number.MAX_VALUE;
                return indexA - indexB;
            })
            .map((w) => {
                switch (w.widgetType) {
                    case DashboardWidgetType.List:
                        return {
                            id: w.id,
                            // header: w.name,
                            body: (
                                <ListWidgetCard
                                    dashboardStore={dashboardStore}
                                    widget={w as ListWidget}
                                    openEditWidgetModal={openEditWidgetModal}
                                />
                            ),
                            reorderable: !w.isDefaultWidget,
                        };
                    case DashboardWidgetType.PieChart:
                        return {
                            id: w.id,
                            header: w.name,
                            body: (
                                <PieWidgetCard
                                    dashboardStore={dashboardStore}
                                    widget={w as PieChartWidget}
                                    openEditWidgetModal={openEditWidgetModal}
                                />
                            ),
                            reorderable: true,
                        };
                    case DashboardWidgetType.Grouped:
                        return {
                            id: w.id,
                            header: w.name,
                            body: (
                                <GroupedWidgetsCard
                                    dashboardStore={dashboardStore}
                                    widget={w as GroupedWidget}
                                    openEditWidgetModal={
                                        openEditGroupedWidgetModal
                                    }
                                />
                            ),
                            reorderable: true,
                        };
                    case DashboardWidgetType.Numeric:
                        return {
                            id: w.id,
                            header: w.name,
                            body: (
                                <NumericWidgetCard
                                    dashboardStore={dashboardStore}
                                    widget={w as NumericWidget}
                                    openEditWidgetModal={openEditWidgetModal}
                                />
                            ),
                            reorderable: true,
                        };
                    default:
                        return { header: w.name, body: <></> };
                }
            });

        if (dashboardStore.currentView === DEFAULT_DASHBOARD_VIEW_KEY) {
            widgets.unshift({
                id: "activities",
                header: "Activities",
                body: (
                    <ActivitiesCard
                        dashboardStore={dashboardStore}
                        tasksStore={taskPreviewVisualStore}
                    />
                ),
                reorderable: false,
            });
        }

        const handleReposition = (e: TileLayoutRepositionEvent) => {
            const updatedPositions = e.value.map((position, index) => {
                // Lock the Activities tile in its original position
                if (dashboardStore.currentView === DEFAULT_DASHBOARD_VIEW_KEY) {
                    if (index === 0) {
                        return tilePositions[0]; // Keep the original position of the Activities tile
                    }
                    if (index === 1) {
                        const newPosition = { row: 1, ...tilePositions[1] };
                        return newPosition; // Keep the original position of the Activities tile
                    }
                }

                return position;
            });
            setTilePositions(updatedPositions);
            localStorage.setItem(
                dashboardStore.currentView,
                JSON.stringify(updatedPositions),
            );
        };

        return (
            <div className="widgets-container">
                <TileLayout
                    // autoFlow="row dense"
                    columns={3}
                    rowHeight={350}
                    positions={tilePositions}
                    gap={{ rows: 10, columns: 10 }}
                    items={widgets}
                    onReposition={handleReposition}
                />
            </div>
        );
    };

    const getEditViewMenu = (view: string) => {
        const items = [
            {
                key: "edit",
                label: (
                    <div>
                        <i className="alpha-icon edit-pen xs" />
                        <span>Edit</span>
                    </div>
                ),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onClick: (e: any) => {
                    e.domEvent.stopPropagation();
                    setEditableView(view);
                },
            },
        ];
        if (view !== DEFAULT_DASHBOARD_VIEW_KEY) {
            items.push({
                key: "remove",
                label: (
                    <Popconfirm
                        title="Are you sure you want to remove this view?"
                        onConfirm={() => dashboardStore.deleteView(view)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <div>
                            <i className="alpha-icon danger-delete-icon xs" />
                            <span>Remove</span>
                        </div>
                    </Popconfirm>
                ),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onClick: (e: any) => {
                    e.domEvent.stopPropagation();
                },
            });
        }
        return { items };
    };

    const handleMenuOpenClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const getViewLabel = (view: string) => {
        const viewName =
            view === DEFAULT_DASHBOARD_VIEW_KEY ? "Default view" : view;
        return (
            <div className="view-label-wrapper">
                <span>{viewName}</span>
                <span className="edit-view-dots">
                    <Dropdown
                        trigger={["click"]}
                        menu={getEditViewMenu(view)}
                        overlayClassName="alpha-dropdown-overlay view-editor"
                    >
                        <span onClick={handleMenuOpenClick}>...</span>
                    </Dropdown>
                </span>
            </div>
        );
    };

    if (dashboardStore.isInitialLoading || widgetConstructorStore.isLoading) {
        return <LoadingIndicator />;
    }

    return (
        <Layout className="screen-size layout-with-table">
            <div className="dashboard-container">
                <div className="header-wrapper">
                    <div className="title-wrapper">
                        <div className="header-title">Dashboard</div>
                    </div>
                    <Button
                        data-id="button-refresh-widgets"
                        onClick={dashboardStore.refreshDashboard}
                        className="refresh-button"
                        type="text"
                        size="large"
                        icon={<i className="alpha-icon refresh" />}
                    >
                        Refresh widgets
                    </Button>
                    <Button
                        data-id="button-add-type"
                        type="primary"
                        size="large"
                        onClick={() => setIsAddWidgetDialogVisible(true)}
                    >
                        Add widget
                    </Button>
                </div>
                <AddWidgetDialog
                    store={dashboardStore}
                    widgetStore={widgetConstructorStore}
                    isVisible={isAddWidgetDialogVisible}
                    closeDialog={() => setIsAddWidgetDialogVisible(false)}
                />
                {(isUpdateViewDialogVisible || !!editableView) && (
                    <UpdateViewDialog
                        viewName={editableView}
                        store={dashboardStore}
                        widgetStore={widgetConstructorStore}
                        closeDialog={() => {
                            setEditableView("");
                            setIsUpdateViewDialogVisible(false);
                        }}
                    />
                )}
                <CreateWidgetDialog
                    isVisible={isEditModalVisible}
                    closeDialog={() => setIsEditModalVisible(false)}
                    updateCallBack={dashboardStore.refreshDashboard}
                />
                <CreateGroupedWidgetDialog
                    isVisible={isEditGroupedWidgetModalVisible}
                    closeDialog={() =>
                        setIsEditGroupedWidgetModalVisible(false)
                    }
                    updateCallBack={dashboardStore.refreshDashboard}
                />
                <Tabs
                    activeKey={dashboardStore.currentView}
                    items={dashboardStore.views.map((v) => ({
                        label: getViewLabel(v.name),
                        key: v.name,
                        children: getWidgetsContainer(),
                    }))}
                    onChange={(name) => {
                        dashboardStore.changeCurrentView(name);
                    }}
                    renderTabBar={(props, DefaultTabBar) => (
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <DefaultTabBar {...props} />
                            <div
                                className="new-view-button"
                                onClick={() =>
                                    setIsUpdateViewDialogVisible(true)
                                }
                            >
                                + New view
                            </div>
                        </div>
                    )}
                />
            </div>
        </Layout>
    );
};

export default observer(DashboardPage);
